/**
 * Blockierte WhatsApp-Kontakte
 * Listet die auf WhatsApp gesperrten Kontakte und erlaubt das Entsperren.
 * Zugriff: Admin und Manager.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Paper,
  Typography,
  Box,
  Stack,
  Button,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  Block as BlockIcon,
  LockOpen as LockOpenIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { getApiBaseUrl } from "../../utils/environment";
import { getAuthToken } from "../../services/apiClient";

const DEFAULT_TENANT_ID = "hobbygarage-frankenthal";

interface BlockedContact {
  chatId: string;
  number: string;
  name: string;
  sessionId?: string;
  blockedReason?: string | null;
  blockedAt?: string | null;
}

// Macht aus einer Roh-Nummer ("491512345678" oder "...@c.us") eine lesbare Form.
function formatNumber(value: string): string {
  const digits = String(value || "").replace(/@.*/, "").replace(/[^\d]/g, "");
  return digits ? `+${digits}` : "Unbekannter Kontakt";
}

function formatDate(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const BlockedContactsPage: React.FC = () => {
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const tenantId = DEFAULT_TENANT_ID;

  const [contacts, setContacts] = useState<BlockedContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [unblockingId, setUnblockingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<BlockedContact | null>(null);

  const request = useCallback(
    async (path: string, options: RequestInit = {}) => {
      const token = getAuthToken();
      const response = await fetch(`${apiBaseUrl}${path}`, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          ...(options.headers ?? {}),
        },
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error ?? `HTTP ${response.status}`);
      }
      if (response.status === 204) return null;
      return response.json();
    },
    [apiBaseUrl]
  );

  const loadContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body = await request(
        `/chat/whatsapp/contacts/blocked?tenantId=${encodeURIComponent(tenantId)}`
      );
      setContacts(Array.isArray(body?.data) ? body.data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Blockierte Kontakte konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [request, tenantId]);

  useEffect(() => {
    void loadContacts();
  }, [loadContacts]);

  const handleUnblock = useCallback(
    async (contact: BlockedContact) => {
      setUnblockingId(contact.chatId);
      setError(null);
      setNotice(null);
      try {
        await request(`/chat/whatsapp/contacts/unblock`, {
          method: "POST",
          body: JSON.stringify({ tenantId, phoneNumber: contact.number || contact.chatId }),
        });
        setContacts((prev) => prev.filter((entry) => entry.chatId !== contact.chatId));
        setNotice(`${contact.name || formatNumber(contact.number || contact.chatId)} wurde entsperrt.`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Kontakt konnte nicht entsperrt werden");
      } finally {
        setUnblockingId(null);
        setConfirmTarget(null);
      }
    },
    [request, tenantId]
  );

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", sm: "center" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <BlockIcon color="error" />
              <Typography variant="h5" component="h1">
                Blockierte Kontakte
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Auf WhatsApp gesperrte Kontakte – z. B. nach Beleidigungen. Hier kannst du
              die Sperre wieder aufheben.
            </Typography>
          </Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void loadContacts()}
            disabled={loading}
            variant="outlined"
          >
            Aktualisieren
          </Button>
        </Stack>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {notice && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
            {notice}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : contacts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <BlockIcon sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
            <Typography>Aktuell sind keine Kontakte blockiert.</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {contacts.map((contact, index) => (
              <React.Fragment key={contact.chatId}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{ px: 0, flexWrap: "wrap", gap: 1 }}
                  secondaryAction={
                    <Tooltip title="Sperre aufheben">
                      <span>
                        <IconButton
                          edge="end"
                          color="primary"
                          disabled={unblockingId !== null}
                          onClick={() => setConfirmTarget(contact)}
                        >
                          {unblockingId === contact.chatId ? (
                            <CircularProgress size={20} />
                          ) : (
                            <LockOpenIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                >
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Typography fontWeight={600}>
                          {contact.name || formatNumber(contact.number || contact.chatId)}
                        </Typography>
                        {contact.name && (
                          <Chip size="small" label={formatNumber(contact.number || contact.chatId)} />
                        )}
                      </Stack>
                    }
                    secondary={
                      <>
                        {contact.blockedReason && (
                          <Typography variant="body2" color="text.secondary" component="span">
                            Grund: {contact.blockedReason}
                          </Typography>
                        )}
                        {contact.blockedAt && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                            sx={{ display: "block" }}
                          >
                            Gesperrt am {formatDate(contact.blockedAt)}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      <Dialog open={confirmTarget !== null} onClose={() => setConfirmTarget(null)}>
        <DialogTitle>Sperre aufheben?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmTarget &&
              `${confirmTarget.name || formatNumber(confirmTarget.number || confirmTarget.chatId)} wird wieder entsperrt und kann erneut schreiben.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmTarget(null)} disabled={unblockingId !== null}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={unblockingId !== null}
            onClick={() => confirmTarget && void handleUnblock(confirmTarget)}
          >
            Entsperren
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BlockedContactsPage;
